import { FinalizeBroker, PAGES } from "@dune/types";
import { Button, VerticalStepper } from "@dune/ui";
import { LogOut } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	useFinalizeBroker,
	useFinalizeBrokerForId,
} from "../../../api/mutations/societies.mutations";
import { useMe } from "../../../api/queries/users.queries";
import { useAuthStore } from "../../../stores/auth.store";
import CompleteFiles from "./CompleteFiles";
import CompleteInformations from "./CompleteInformations";
import SendAll from "./SendAll";
import {
	RegisterSecondStepDetailsFormData,
	registerSecondStepDetailsFormSchema,
	useRegisterSecondStepDetailsForm,
} from "./UseRegisterSecondStepBroker";
import {
	RegisterSecondStepFilesFormData,
	useRegisterFilesForSecondStepForm,
} from "./useRegisterFilesForSecondStepForm";
import { RoleEnum } from "../../../../../../packages/vars/src";

export default function RegisterSecondStepForSociety() {
	const { brokerId } = useParams<{ brokerId?: string }>();
	const { me: currentUser, isLoading: isLoadingUser } = useMe();
	const [activeStep, setActiveStep] = useState(2);
	const [isCompleted, setIsCompleted] = useState(false);
	const { mutate: finalizeRegister } = useFinalizeBroker();
	const { mutate: finalizeRegisterForId } = useFinalizeBrokerForId();
	const logout = useAuthStore((state) => state.logout);
	const navigate = useNavigate();

	const { form, isLoading } = useRegisterSecondStepDetailsForm(brokerId);
	const { control, handleSubmit, formState, watch, getValues } = form;
	const { errors } = formState;

	const {
		control: filesControl,
		handleSubmit: filesHandleSubmit,
		errors: filesErrors,
		isLoading: filesIsLoading,
		getValues: filesGetValues,
		setValue: filesSetValue,
		isLoadingFiles,
	} = useRegisterFilesForSecondStepForm(getValues("categories"), brokerId);

	const hasAccessToBrokerId = currentUser?.role === RoleEnum.DUNE || !brokerId;

	if (isLoadingUser) {
		return (
			<div className="flex items-center justify-center h-screen">
				<h1 className="text-3xl font-semibold">Chargement...</h1>
			</div>
		);
	}

	if (!hasAccessToBrokerId) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div className="text-center">
					<h1 className="text-3xl font-semibold text-red-600 mb-4">
						Accès non autorisé
					</h1>
					<p className="text-gray-600 mb-6">
						Vous n'êtes pas autorisé à modifier les informations de ce broker.
					</p>
					<Button onClick={() => navigate(PAGES.ROOT)}>
						Retour à l'accueil
					</Button>
				</div>
			</div>
		);
	}

	const handleLogout = () => {
		logout();
		navigate(PAGES.LOGIN);
	};

	const onSubmitUserDetails = async () => {
		try {
			changeActiveStep(activeStep);
		} catch (error) {
			console.error(
				"Erreur lors de la validation des détails utilisateur :",
				error,
			);
		}
	};

	const executeFinalSubmission = async (
		detailsData: RegisterSecondStepDetailsFormData,
		files: RegisterSecondStepFilesFormData,
	) => {
		try {
			const user: FinalizeBroker["user"] = {
				email: detailsData.email,
				firstname: detailsData.firstname,
				lastname: detailsData.lastname,
				position: detailsData.position,
				mobilePhone: detailsData.phone,
				hasSubscribedToNewsletter: detailsData.newsletter ?? false,
				isSignatory: detailsData.isSignatory,
				howDidYouKnowDune: detailsData.howDidYouKnowDune,
			};

			if (detailsData.landlinePhone !== undefined) {
				user.landlinePhone = detailsData.landlinePhone;
			}
			if (detailsData.firstnameSignatory !== undefined) {
				user.firstnameSignatory = detailsData.firstnameSignatory;
			}
			if (detailsData.lastnameSignatory !== undefined) {
				user.lastnameSignatory = detailsData.lastnameSignatory;
			}
			if (detailsData.landlinePhoneSignatory !== undefined) {
				user.landlinePhoneSignatory = detailsData.landlinePhoneSignatory;
			}
			if (detailsData.phoneSignatory !== undefined) {
				user.mobilePhoneSignatory = detailsData.phoneSignatory;
			}
			if (detailsData.positionSignatory !== undefined) {
				user.positionSignatory = detailsData.positionSignatory;
			}
			if (detailsData.emailSignatory !== undefined) {
				user.emailSignatory = detailsData.emailSignatory;
			}

			const society = {
				kbis: { ...files.kbis, sizeInBytes: files.kbis.size },
				identity: { ...files.identity, sizeInBytes: files.identity.size },
				rib: { ...files.rib, sizeInBytes: files.rib.size },
				rc: { ...files.rc, sizeInBytes: files.rc.size },
				...(files.guarantee && {
					guarantee: { ...files.guarantee, sizeInBytes: files.guarantee.size },
				}),
			};
			const broker: FinalizeBroker = {
				society,
				user,
			};

			if (brokerId) {
				finalizeRegisterForId({ brokerId, broker });
			} else {
				finalizeRegister(broker);
			}
			changeActiveStep(activeStep);
		} catch (error) {
			console.error("Erreur lors de la soumission finale :", error);
		}
	};

	const triggerFinalSubmitHandler = async (
		files: RegisterSecondStepFilesFormData,
	) => {
		await handleSubmit((detailsData) =>
			executeFinalSubmission(detailsData, files),
		)();
	};

	const changeActiveStep = async (step: number) => {
		setActiveStep(step + 1);
		if (step + 1 === 4) {
			setIsCompleted(true);
		}
	};

	const backToSecondStep = () => {
		setIsCompleted(false);
		setActiveStep(2);
	};

	return (
		<div className="flex h-screen">
			<div className="fixed top-0 left-0 w-[280px] h-full bg-background-input-gray p-6 flex flex-col gap-12">
				<img src="/dune_logo.png" alt="Dune Logo" className="w-32 mb-8" />
				<VerticalStepper
					steps={[
						{ step: 1, title: "Numéro ORIAS" },
						{ step: 2, title: "Mes informations" },
						{ step: 3, title: "Pièces justificatives" },
					]}
					stepSelected={activeStep}
					onStepSelected={(step) => setActiveStep(step)}
					isStepDisabled={(step) => {
						if (step === 1) return true;
						if (step === 2) return false;
						if (step === 3) {
							const isValid = registerSecondStepDetailsFormSchema.safeParse(
								getValues(),
							);
							return !isValid.success;
						}
						return false;
					}}
				/>
			</div>
			<div className="flex-1 ml-[280px] flex flex-col">
				<div className="sticky top-0 z-10 flex justify-end items-center p-4 border-b bg-white">
					<Button
						variant="ghost"
						onClick={handleLogout}
						className="text-red-600 hover:text-red-700 hover:bg-red-50"
					>
						<LogOut className="mr-2 h-4 w-4" />
						Se déconnecter
					</Button>
				</div>
				<div className="flex-1 flex justify-center">
					{!isCompleted && activeStep === 2 && (
						<CompleteInformations
							isLoading={isLoading}
							control={control}
							errors={errors}
							watch={watch}
							handleSubmit={handleSubmit}
							onSubmit={onSubmitUserDetails}
						/>
					)}
					{!isCompleted && activeStep === 3 && (
						<CompleteFiles
							backStep={backToSecondStep}
							onFinalize={triggerFinalSubmitHandler}
							categoriesForFileForm={getValues("categories")}
							filesForm={filesGetValues()}
							control={filesControl}
							errors={filesErrors}
							isLoading={filesIsLoading || isLoadingFiles}
							handleSubmit={filesHandleSubmit}
							setValue={filesSetValue}
						/>
					)}
					{isCompleted && <SendAll backStep={backToSecondStep} />}
				</div>
			</div>
		</div>
	);
}
